import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import type { PMIssueStatus } from "@/domains/pm/types";
import { countIssuesWithStatus, deleteIssueStatus } from "@/stores/pm/repository";
import { StatusBadge } from "@/domains/pm/components/shared/StatusBadge";

/**
 * Deleting a custom status.
 *
 * The database refuses while any live issue still holds the key — the column
 * is text, not a foreign key, so nothing else would stop it and the issues
 * would be left carrying a status the validator now rejects, i.e. unwritable.
 * Rather than let the user discover that as an error, the dialog counts the
 * issues first and says plainly that they have to be moved.
 */
export function DeleteStatusDialog({
  open,
  onOpenChange,
  status,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: PMIssueStatus | null;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.statuses;
  const [deleting, setDeleting] = useState(false);

  const { data: inUse, isLoading } = useQuery({
    queryKey: ["pm-status-usage", status?.building_project_id, status?.key],
    queryFn: () =>
      countIssuesWithStatus(status?.building_project_id as number, status?.key as string),
    enabled: open && status != null,
    staleTime: 0,
  });

  const blocked = (inUse ?? 0) > 0;

  const confirm = async () => {
    if (!status || blocked) return;
    setDeleting(true);
    try {
      await deleteIssueStatus(status.id);
      onOpenChange(false);
    } catch (e) {
      toast.error(friendlyIssueError(e, t));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.deleteTitle}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              {status && (
                <StatusBadge
                  status={status.key}
                  buildingProjectId={status.building_project_id}
                  variant="pill"
                />
              )}
              <p>
                {isLoading
                  ? copy.deleteChecking
                  : blocked
                    ? copy.deleteInUse.replace("{count}", String(inUse))
                    : copy.deleteConfirm}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onOpenChange(false)}>
            {t.common.cancel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void confirm();
            }}
            disabled={blocked || isLoading || deleting}
            className="bg-destructive hover:bg-destructive/90"
          >
            {t.common.delete}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
